/* eslint-disable no-shadow */
// @flow

import { useMemo, useState } from 'react';

import { CornerDownRight } from 'lucide-react';

import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  updateCartProduct,
  getCart,
  type DecoratedProduct,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Fqdn, Text } from '@gandi/ui';

import type { GandiFormSelectOption } from 'src/Domain/types';
import type { IDNLangAlpha2Code } from 'src/DomainCreate/types';
import { useNotifications, useAppSelector } from 'src/hooks';

import { ListRow } from '../../ListRow';

import { WebHostingSizeError } from './WebHostingSizeError';
import ProductItem from './components/ProductItem';
import { AlphabetRow, PlanToggleGroupRow } from './components/Rows';
import { WebHostingSizeToggleGroupRow } from './components/Rows/WebHostingSizeToggleGroupRow';
import { useWebhostingDiffPrices } from './components/modules/webhosting';
import { usePlanOptions } from './modules/hooks';

export type Props = {
  product: DecoratedProduct,
};

export default function WebHostingCreate({
  product,
}: Props): React.MixedElement {
  const dispatch = useDispatch();
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [isUpdatingSize, setIsUpdatingSize] = useState(false);
  const [isPatchingLang, setIsPatchingLang] = useState(false);

  const { __ } = useTranslator();
  const { notifyMaybeApiError } = useNotifications();

  const diffMonthly = useWebhostingDiffPrices(product.payload.size);

  // errors
  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const { filteredProductErrors, sizeError } = useMemo(() => {
    const filtered = productErrors
      // solved by the contact wizard
      .filter(error => !/(owner|admin|bill|tech).*/.test(error.name))
      // solved by alphabet row
      .filter(err => err.name !== 'lang')
      // extra-params errors require_x-uk_contact_type
      .filter(error => !/\.x-/.test(error.name));

    // To exclude essential error
    const sizeErrorIndex = filtered.findIndex(error => error.name === 'size');
    let sizeError;
    let remainingErrors = filtered;

    if (sizeErrorIndex > -1) {
      sizeError = filtered[sizeErrorIndex];
      remainingErrors = filtered.filter((_, index) => index !== sizeErrorIndex);
    }

    return {
      filteredProductErrors: remainingErrors,
      sizeError,
    };
  }, [productErrors]);

  const langError = productErrors.some(err => err.name === 'lang');

  /// size change
  const updateSizeHandler = async (evt: SyntheticInputEvent<>) => {
    evt.preventDefault();

    // use an internal flag to avoid brutal price switching
    setIsUpdatingSize(true);

    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          // $FlowFixMe[incompatible-call]  value is set by options
          payload: { ...product.payload, size: evt.target.value },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsUpdatingSize(false);
  };

  // plan change
  const planOptions = usePlanOptions(product.payload, diffMonthly);

  const updatePlanHandler = async (evt: SyntheticInputEvent<>) => {
    evt.preventDefault();

    // use an internal flag to avoid brutal price switching
    setIsUpdatingPlan(true);

    // the value is always a string
    // here we are passing a duration
    // it has to be a number kind
    const duration = parseInt(evt.target.value, 10);

    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: { ...product.payload, duration },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsUpdatingPlan(false);
  };

  // domain lang / alphabet
  const handleLangChange = async (
    selectedOption: GandiFormSelectOption<IDNLangAlpha2Code>,
  ): Promise<void> => {
    setIsPatchingLang(true);

    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: {
            ...product.payload,
            // $FlowFixMe[prop-missing] flow thinks that domain.fqdn is missing
            domain: {
              ...product.payload.domain,
              lang: selectedOption.value,
            },
          },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsPatchingLang(false);
  };

  const label = __('Web Hosting');

  const price =
    product.isWorking ||
    isUpdatingSize ||
    isUpdatingPlan ||
    // product.total?.before_taxes === 0 when there is a lang error
    (productErrors.length > 0 && product.total?.before_taxes === 0)
      ? undefined
      : product.total?.before_taxes;

  const fqdn = product.payload.domain?.fqdn && (
    <Fqdn color="base">{product.payload.domain.fqdn}</Fqdn>
  );

  return (
    <ProductItem
      label={label}
      productId={product.id}
      priceBeforeTaxes={!sizeError ? price : undefined}
      hasTaxes={product.hasTaxes}
      header={
        <>
          {sizeError ? (
            <WebHostingSizeError
              error={sizeError}
              // $FlowFixMe[incompatible-type]
              size={product.payload.size}
              product={product}
            />
          ) : (
            <>
              <PlanToggleGroupRow
                options={planOptions}
                value={product.payload.duration || 1}
                onChange={updatePlanHandler}
                productId={product.id}
              />
              <WebHostingSizeToggleGroupRow
                isBusy={isUpdatingSize}
                product={product}
                updateSizeHandler={updateSizeHandler}
                removeLowerThanActual
              />
            </>
          )}
          {fqdn && (langError || product.payload.domain?.lang) && (
            <AlphabetRow
              disabledOption={product.isWorking || isPatchingLang}
              productId={product.id}
              value={product.payload.domain?.lang}
              // $FlowFixMe[incompatible-type]
              onChange={handleLangChange}
            />
          )}
        </>
      }
      errors={filteredProductErrors}
    >
      {fqdn && (
        <ListRow
          icon={<CornerDownRight size={16} />}
          title={fqdn}
          price={<Text color="weak">{__('Included')}</Text>}
        />
      )}
    </ProductItem>
  );
}
